import React, { useState } from 'react';
import styled from '@emotion/styled';
import * as Yup from 'yup';

import ModalContainer from '../ModalContainer';
import { useContext } from 'react';
import { ModalContext } from '../../../context/ModalContext';
import { FormComponent } from '../../Form/FormComponent';
import ValidatingFormSubmitButton from '../../Button/FormSubmitButton';
import { CustomRadio, CustomRadioGroup } from '../../Form/CustomRadioField';
import { useJobSearch } from '../../../context/JobSearchContext';

// Array of all Nigerian states + FCT (shown as Abuja)
const nigeriaStates = [
  'Abia',
  'Abuja',
  'Adamawa',
  'Akwa Ibom',
  'Anambra',
  'Bauchi',
  'Bayelsa',
  'Benue',
  'Borno',
  'Cross River',
  'Delta',
  'Ebonyi',
  'Edo',
  'Ekiti',
  'Enugu',
  'Gombe',
  'Imo',
  'Jigawa',
  'Kaduna',
  'Kano',
  'Katsina',
  'Kebbi',
  'Kogi',
  'Kwara',
  'Lagos',
  'Nasarawa',
  'Niger',
  'Ogun',
  'Ondo',
  'Osun',
  'Oyo',
  'Plateau',
  'Rivers',
  'Sokoto',
  'Taraba',
  'Yobe',
  'Zamfara',
];

// Ensure the states are sorted alphabetically
nigeriaStates.sort();

// Array for pay intervals with proper values
const payIntervals = [
  { label: 'Per hour', value: 'HOURLY' },
  { label: 'Per day', value: 'DAILY' },
  { label: 'Per week', value: 'WEEKLY' },
  { label: 'Per month', value: 'MONTHLY' },
];

const initialValues = {
  location: 'Any',
  payInterval: 'HOURLY',
};

const validationSchema = Yup.object().shape({
  location: Yup.string().required('Location is required'),
  payInterval: Yup.string().required('Pay Interval is required'),
});

const JobFilterModal: React.FC = () => {
  const [showAllStates, setShowAllStates] = useState(false);
  const modalContext = useContext(ModalContext);

  const { setPage, setLocation, setPayInterval } = useJobSearch();

  // Display first five if not expanded; otherwise, display all states.
  const statesToDisplay = showAllStates
    ? nigeriaStates
    : nigeriaStates.slice(0, 5);

  const toggleShowStates = () => {
    setShowAllStates((prev) => !prev);
  };

  const onSubmit = (values: any) => {
    setPage('1');
    setLocation(values.location.toUpperCase() === 'ANY' ? '' : values.location);
    setPayInterval(
      values.payInterval.toUpperCase() === 'ANY' ? '' : values.payInterval
    );
    modalContext?.displayModal(false);
  };

  return (
    <ModalContainer width="30rem" padding="2rem" marginTop="3rem">
      <Header>Job Filter</Header>
      <Container>
        <FormComponent
          initialValues={initialValues}
          schema={validationSchema}
          onSubmit={onSubmit}
        >
          <Section>
            <h4>Location</h4>
            <CustomRadioGroup name="location">
              <CustomRadio name="location" value="Any">
                Any
              </CustomRadio>
              {statesToDisplay.map((state) => (
                <CustomRadio key={state} name="location" value={state}>
                  {state}
                </CustomRadio>
              ))}
              <ToggleText onClick={toggleShowStates}>
                {showAllStates ? 'Less' : 'More (show 37 locations)'}
              </ToggleText>
            </CustomRadioGroup>
          </Section>
          <Section>
            <h4>JobType</h4>
            <CustomRadioGroup name="payInterval">
              <CustomRadio name="payInterval" value="Any">
                Any
              </CustomRadio>
              {payIntervals.map((interval) => (
                <CustomRadio
                  key={interval.value}
                  name="payInterval"
                  value={interval.value}
                >
                  {interval.label}
                </CustomRadio>
              ))}
            </CustomRadioGroup>
          </Section>
          <ValidatingFormSubmitButton className="fillParent">
            Apply
          </ValidatingFormSubmitButton>
        </FormComponent>
      </Container>
    </ModalContainer>
  );
};

export default JobFilterModal;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.5rem;
  justify-content: center;
  align-items: center;
  max-width: 600px;
`;

const Section = styled.div`
  & > h4 {
    font-weight: 400;
    font-size: 24px;
    line-height: 33.12px;
    letter-spacing: -0.9px;
    margin-bottom: 2rem;
  }
`;

const Header = styled.div`
  font-weight: 400;
  font-size: 32px;
  line-height: 44.16px;
  letter-spacing: -4%;
`;

const ToggleText = styled.span`
  font-weight: 400;
  font-size: 20px;
  line-height: 27.6px;
  letter-spacing: -0.9px;
  cursor: pointer;
  text-decoration: underline;
`;
